import { Box, Container, Flex, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { formatPrice } from '@getjust/commons';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { CheckoutTheme } from '@getjust/leaf';

import { bottomSheetOpenCountAtom } from '$business/atoms/bottom-sheet';
import { Header } from '$components/Header';
import { LanguageSelection } from '$components/LanguageSelection';
import { AuthStatuses, useAuth } from '$src/hooks/auth';
import { LoadingWrapper } from '$components/LoadingWrapper';
import { ProgressBar } from '$components/ProgressBar';
import { BrandLogo } from '$src/components/BrandLogo';
import { CartSkeleton } from '$src/components/CartSkeleton';
import ExitIntentPopup from '$src/components/ExitIntentPopup';
import { LoyaltyRewards } from '$src/components/LoyaltyRewards';
import { MobileCartSummary } from '$src/components/MobileCartSummary';
import { Upsell } from '$src/components/Upsell';
import { REFS_MAP } from '$src/constants';
import { ExitIntentContext } from '$src/context/ExitIntentContext';
import { useThemeContext } from '$src/context/ThemeContext';
import { useExitIntent } from '$src/hooks/client/useExitIntent';
import { useIsDesktop } from '$src/hooks/client/useIsDesktop';
import { useCart, useFeatureFlag, useShop } from '$src/hooks/queries';
import { useROBrandingPreviewAtom } from '$src/hooks/state/useBrandingPreviewAtom';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';
import { useLoadingAtom, usePaidLoadingAtom } from '$src/hooks/state/useLoadingAtom';
import { getScrollbarStyles, getThemeColors } from '$src/utils/themeColors';

const collapseHeader = keyframes`
  0% { height: 4.5rem; }
  100% { height: 3.5rem; }
`;

const expandHeader = keyframes`
  0% { height: 3.5rem; }
  100% { height: 4.5rem; }
`;

const collapseCartSummary = keyframes`
  from { height: 100%; }
  to { height: 0; }
`;

const expandCartSummary = keyframes`
  from { height: 0; }
  to { height: 100%; }
`;

const fadeInCartSummary = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const fadeOutCartSummary = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

type Props = {
  children: React.ReactNode;
};

export const Layout = ({ children }: Props) => {
  const { isLargerThan750, isLargerThan900, isMobile } = useIsDesktop();
  const { authState } = useAuth();
  const { loading } = useLoadingAtom();
  const { paidLoading } = usePaidLoadingAtom();
  const { data: cart, status: useCartStatus, isLoading: cartLoading } = useCart();
  const { data: shop } = useShop();
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();
  const { themeName } = useThemeContext();
  const brandingPreview = useROBrandingPreviewAtom();
  const colors = useMemo(() => getThemeColors(themeName), [themeName]);
  const { t } = useTranslation('common');
  const currency = cart?.currency ?? 'EUR';
  const totalDue = formatPrice(cart?.totalAmount ?? 0, { currency });
  const background = brandingPreview?.brandStyles?.background ?? shop?.settings?.brandStyles?.background;
  const isTransparentCustom = themeName === 'custom' && background === 'transparent';
  const isGlassy = themeName === 'glassy' || isTransparentCustom;

  const scrollbarStyles = useMemo(() => getScrollbarStyles(themeName), [themeName]);

  // Theme-aware background colors
  const formBgMap: Record<CheckoutTheme, string> = {
    aurora: colors.bg,
    black: colors.bgSecondary,
    glassy: colors.bgSecondary,
    soft: colors.bg,
    classic: colors.bg,
    // White right column (the left cart panel carries the grey tint via
    // colors.bgSecondary). Transparent custom is handled by the
    // isTransparentCustom override below, so this only affects the solid case.
    custom: colors.bg,
  };
  const glassyColors = getThemeColors('glassy');
  const formBg = isTransparentCustom ? glassyColors.bgSecondary : formBgMap[themeName];
  const basePaddingBottom = isLargerThan900 ? 12 : isLargerThan750 ? 8 : 2;
  const contentPaddingBottom = isGlassy ? basePaddingBottom + 4 : basePaddingBottom;

  // A/B test: exit intent popup
  // Control group: popup disabled | Enabled variant: popup shown
  const { data: exitIntentVariant, isPending: isExitIntentFlagPending } = useFeatureFlag('exit_intent_popup');
  // Treat as disabled while loading to avoid flash, and only enable if variant is 'enabled'
  const isExitIntentEnabled = !isExitIntentFlagPending && exitIntentVariant === 'enabled';

  const exitIntent = useExitIntent({ isFeatureEnabled: isExitIntentEnabled, shuffleOptions: true });

  const portalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // update tab title
    if (shop?.name && shop.name !== document.title) {
      document.title = shop?.name;
    }
  }, [shop?.name]);

  useEffect(() => {
    if (portalRef.current) {
      REFS_MAP.set('portal', portalRef);
    }
  }, []);

  useEffect(() => {
    // slide animation for mobiles
    if (!portalRef.current) return;
    const myTimeout = setTimeout(() => {
      portalRef.current?.classList.add('slideIn');
    }, 200);
    return () => clearTimeout(myTimeout);
  }, []);

  const [scrollPercentage, setScrollPercentage] = useState<number>(0);
  const mainRef = useRef<HTMLDivElement>(null);
  const bottomSheetOpenCount = useAtomValue(bottomSheetOpenCountAtom);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [isCartSummaryCollapsed, setIsCartSummaryCollapsed] = useState(false);

  useEffect(() => {
    const scrollableDiv = mainRef.current;
    if (!scrollableDiv) return;

    function handleScroll() {
      if (!scrollableDiv) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollableDiv;
      const maxScroll = scrollHeight - clientHeight;
      const scrolled = (scrollTop / maxScroll) * 100;
      setScrollPercentage(scrolled);

      setIsHeaderCollapsed(scrolled >= 2);
      setIsCartSummaryCollapsed(scrolled >= 2);
    }

    scrollableDiv.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollableDiv.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock the background checkout scroll while a mobile bottom sheet is open so
  // touch-scroll stays inside the sheet instead of bleeding to the page behind.
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    el.style.overflow = bottomSheetOpenCount > 0 ? 'hidden' : '';
  }, [bottomSheetOpenCount]);

  const showMobileBackground = canUseCheckoutFeature('show_mobile_background_image') && isMobile;

  const showProgressBar = useMemo(() => {
    if (loading || cartLoading) return false;
    if (!isMobile) return scrollPercentage >= 10;

    return showMobileBackground ? scrollPercentage >= 6 : scrollPercentage >= 3;
  }, [scrollPercentage, showMobileBackground, isMobile, loading, cartLoading]);

  const headerHeight = useMemo(() => {
    if (!isMobile) return '3.75rem';
    if (!showMobileBackground) return '3.5rem';

    return '4.5rem';
  }, [isMobile, showMobileBackground]);

  const animations = useMemo(() => {
    if (!showMobileBackground) return { header: {}, cartSummary: { height: undefined, opacity: undefined } };

    const headerAnimation = isHeaderCollapsed
      ? `${collapseHeader} 200ms ease-out forwards`
      : `${expandHeader} 200ms ease-out forwards`;

    const cartSummaryAnimations = {
      height: isCartSummaryCollapsed
        ? `${collapseCartSummary} 200ms ease-out forwards`
        : `${expandCartSummary} 200ms ease-out forwards`,

      opacity: isCartSummaryCollapsed
        ? `${fadeInCartSummary} 200ms ease-out forwards`
        : `${fadeOutCartSummary} 200ms ease-out forwards`,
    };

    return {
      header: headerAnimation,
      cartSummary: cartSummaryAnimations,
    };
  }, [showMobileBackground, isHeaderCollapsed, isCartSummaryCollapsed]);

  const exitIntentContextValue = useMemo(
    () => ({
      isOpen: exitIntent.isOpen,
      triggerExitIntent: exitIntent.triggerExitIntent,
      onLeave: exitIntent.onLeave,
      onReasonSelected: exitIntent.onReasonSelected,
      setPendingExitCallback: exitIntent.setPendingExitCallback,
    }),
    [exitIntent],
  );

  return (
    <ExitIntentContext.Provider value={exitIntentContextValue}>
      <Box id="just-form-layout" ref={portalRef} sx={{ '--checkout-header-height': headerHeight }}>
        <LoadingWrapper isLoading={loading} isPaidLoading={paidLoading}>
          {/* Header band is mobile-only. On desktop it held just the shop logo,
              whose full-width band ate into the top of the right column and
              clipped the cart — so we drop it entirely on desktop and let both
              columns start at the very top. */}
          {isMobile && (
            <Box
              zIndex={1000}
              id="just-form-header"
              animation={animations.header}
              top="0"
              position="sticky"
              bg={formBg}
              mb={'-2px'}
              boxShadow={isGlassy ? 'inset 0 -1px 0 rgba(255, 255, 255, 0.35)' : undefined}
            >
              <Header h={headerHeight} />
              <ProgressBar hide={!showProgressBar} progress={scrollPercentage} />
            </Box>
          )}
          <Flex
            bg={formBg}
            overflowY="hidden"
            // Desktop: cart on the left, form on the right. The form (Container)
            // is the first child and the cart the second, so row-reverse swaps
            // the columns. Mobile renders only the form, so it's unaffected.
            flexDirection={isMobile ? undefined : 'row-reverse'}
            h={showMobileBackground ? `calc(100% - ${headerHeight})` : undefined}
            boxShadow={isGlassy ? 'inset 0 1px 0 rgba(255, 255, 255, 0.25)' : undefined}
            position="relative"
          >
            <Container
              ref={mainRef}
              pb={contentPaddingBottom}
              pl={isLargerThan900 ? 8 : isLargerThan750 ? 8 : 2}
              pr={isLargerThan900 ? 8 : isLargerThan750 ? 8 : 2}
              maxW="full"
              m="0"
              as="main"
              // Desktop: fill a true 50% half (flex 1 1 0, like the cart's flex:1).
              // The form content fills the half minus the side padding — matching
              // how the cart's tinted block fills its half — so inputs use the
              // available width instead of sitting capped in a sea of white.
              flex={isMobile ? '1 1 auto' : '1 1 0%'}
              minW={0}
              overflow="auto"
              height="100%"
              display="flex"
              flexDirection="column"
              pt={isMobile ? 2 : 3.5}
              w="full"
              sx={scrollbarStyles}
            >
              <Box
                w="full"
                maxW={isLargerThan750 && !isLargerThan900 ? '344px' : 'full'}
                display="flex"
                flexDirection="column"
                flex="1"
                // Desktop: drop the form down by the cart's logo-block height
                // (logo 1.5rem + 1rem gap) so the first form section lines up with
                // the cart's "Total à payer" row rather than the logo above it.
                pt={isMobile ? undefined : 10}
              >
                {children}
                {/* Quiet footer-style language picker. On authenticated mobile
                    it's rendered inline with the legal links instead (see the
                    authenticated page), so suppress it here to avoid a
                    duplicate. Desktop and the unauthenticated mobile flow keep
                    it here. */}
                {!(isMobile && authState === AuthStatuses.AUTHENTICATED) && (
                  <Flex justifyContent="center" pt={4} pb={2}>
                    <LanguageSelection />
                  </Flex>
                )}
                {/* Authenticated mobile uses a position:fixed Place-order bar
                    (MobilePayButtonSticky). This clearance sits after the page
                    content so the legal/language row can scroll clear of the
                    bar rather than being buried behind it. Sized to the bar
                    height (total line + CTA, no secured-payment line) + the iOS
                    home-indicator safe area. */}
                {isMobile && authState === AuthStatuses.AUTHENTICATED && (
                  <Box h="calc(env(safe-area-inset-bottom, 0px) + 148px)" aria-hidden="true" />
                )}
              </Box>
            </Container>
            {!isMobile && (
              <Box
                // The cart summary sits on its own softly-tinted card — separated
                // from the form by tone + space rather than a hard divider. Raised
                // z-index keeps it above the panel; capped height keeps it scrollable.
                zIndex={1100}
                flex="1"
                bg={colors.bgSecondary}
                pt={6}
                px={isLargerThan900 ? 8 : 6}
                // Stretch to the full column height so the tint runs top-to-bottom
                // of the left side (content stays pinned to the top); the form
                // column is taller and sets the height.
                alignSelf="stretch"
                overflow="auto"
                maxH="calc(100dvh - 2rem)"
                // Center the capped-width cart block within the column.
                display="flex"
                justifyContent="center"
                position="relative"
              >
                <Box w="full" maxW="30rem">
                  {/* Brand header: small calm logo, then a single quiet "total due"
                      line (label left / amount right). px matches the cart rows
                      below so the logo shares their left rail. The bold Total row
                      at the bottom of the summary remains the primary anchor. */}
                  <Box mb={6} px={3}>
                    <BrandLogo h="1.5rem" />
                    {cart && (
                      <Flex mt={4} justify="space-between" align="baseline">
                        <Text fontSize="md" fontWeight={500} color={colors.textSecondary}>
                          {t('commons.total_due')}
                        </Text>
                        <Text
                          fontSize="md"
                          fontWeight={600}
                          color={colors.text}
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {totalDue}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                  {useCartStatus !== 'success' && !cart ? (
                    <CartSkeleton showMobileBackground={showMobileBackground} />
                  ) : (
                    <>
                      <MobileCartSummary variant="sidebar" />
                      {/* Loyalty lifted out of the cart card into its own card
                          right below it (desktop only). */}
                      <LoyaltyRewards asCard />
                      {/* Upsell lives here on desktop — anchored to the cart in
                          the left panel, filling the space below the summary,
                          instead of interrupting the address→payment form flow.
                          Email gate dropped since it no longer follows the email
                          field. (Mobile keeps the upsell in the form.) */}
                      <Box mt={6} px={4}>
                        {/* px={4} aligns the upsell title + cards with the cart /
                            loyalty card content rail. Same image-forward grid as
                            mobile: 3 cards visible, up to 5 rendered, swipeable. */}
                        <Upsell requireEmail={false} layout="grid" />
                      </Box>
                    </>
                  )}
                  {/* Real spacer (not padding): a flex+overflow scroll container
                      clips its child's padding-bottom at the scroll end, so the
                      dots would touch the panel edge. An element is honored. */}
                  <Box h={10} flexShrink={0} aria-hidden />
                </Box>
              </Box>
            )}
          </Flex>
        </LoadingWrapper>
        <ExitIntentPopup
          isOpen={exitIntent.isOpen}
          onLeave={exitIntent.onLeave}
          onReasonSelected={exitIntent.onReasonSelected}
          shuffleOptions={exitIntent.shuffleOptions}
        />
      </Box>
    </ExitIntentContext.Provider>
  );
};
